import { Box, Flex, styled, StyleSystemProps } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { Button } from '../../../../../../../Button';
import { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import { BillboardColorMode, BillboardVariants } from './Billboard';
import { pxToRem } from '@withjoy/joykit/theme';

// Base styled components
export const StyledContainer = styled(Box)``;
export const StyledContent = styled(Flex)``;
export const StyledButtonsContainer = styled(Box)``;
export const StyledHed = styled(Box)``;
export const StyledEyebrow = styled(Box)``;
export const StyledSubtitle = styled(Box)``;
export const StyledCta = styled(Button)``;
export const StyledBlockText = styled(Flex)``;

const colorModes: Record<BillboardColorMode, CSSObject> = {
  light: {
    color: 'white',
    ['& button']: {
      color: 'white',
      borderColor: 'white',
      _hover: {
        color: 'white',
        borderColor: 'white'
      }
    }
  },
  dark: {
    color: 'mono14',
    ['& button']: {
      color: 'mono14',
      borderColor: 'mono14',
      _hover: {
        color: 'mono14',
        borderColor: 'mono14'
      }
    }
  }
};

const alignmentConfig: Record<string, StyleSystemProps> = {
  left: { alignItems: 'flex-start', textAlign: 'start' },
  center: { alignItems: 'center', textAlign: 'center' },
  right: { alignItems: 'flex-end', textAlign: 'end' }
};

/* Container Styles */

const containerVariants: Record<BillboardVariants, CSSObject> = {
  default: {
    paddingBottom: [pxToRem(56), null, pxToRem(88)]
  },
  overviewSlider: {
    paddingBottom: pxToRem(56)
  },
  overviewCard: {
    paddingBottom: 6
  }
};
const containerStyles = (variant: BillboardVariants, alignItems?: 'left' | 'center' | 'right', isDotSliderItem?: boolean): StyleSystemProps => ({
  paddingBottom: isDotSliderItem ? containerVariants[variant]?.paddingBottom : 6,
  width: '100%',
  height: '100%',
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  zIndex: 1,
  paddingX: {
    _: 3,
    sm: 7
  },
  ...alignmentConfig[alignItems || 'center']
});

/* Content Styles */

const contentStyles = (colorMode: BillboardColorMode, alignItems?: 'left' | 'center' | 'right'): StyleSystemProps => {
  return {
    ...alignmentConfig[alignItems || 'center'],
    ...colorModes[colorMode],
    flexDirection: 'column',
    zIndex: 3,
    width: '100%'
  };
};

/* Block Text Styles */

const blockTextStyles = (alignItems?: 'left' | 'center' | 'right', variant?: BillboardVariants): StyleSystemProps => ({
  ...alignmentConfig[alignItems || 'center'],
  flexDirection: 'column',
  marginX: variant === 'overviewSlider' ? [6, null, 3] : 3,
  maxWidth: '100%'
});

/* Head Styles */

const hedVariants: Record<BillboardVariants, StyleSystemProps> = {
  default: {
    typographyVariant: {
      _: 'HedMobile32',
      xs: 'Hed56',
      lg: 'HedHero72',
      xl: 'HedBreak96'
    }
  },
  overviewSlider: {
    typographyVariant: {
      _: 'HedMobile32',
      xs: 'Hed56',
      lg: 'HedHero72',
      xl: 'HedBreak96'
    }
  },
  overviewCard: {
    marginBottom: 7,
    typographyVariant: {
      _: 'HedMobile32',
      xs: 'Hed40',
      lg: 'Hed40',
      xl: 'Hed40'
    }
  }
};
const hedStyles = (variant: BillboardVariants): StyleSystemProps => ({
  ...hedVariants[variant],
  fontWeight: ['light', null, 'light'],
  transition: animationTransition('color'),
  maxWidth: pxToRem(995),
  marginX: 6,
  width: '100%',
  whiteSpace: 'break-spaces'
});

/* Hed Slider Styles */

const hedSliderVariants: Record<BillboardVariants, StyleSystemProps> = {
  default: {
    marginBottom: 5,
    typographyVariant: {
      _: 'Hed40',
      md: 'Hed56'
    }
  },
  overviewSlider: {
    marginBottom: { _: 7, sm: 6, md: 7 },
    typographyVariant: {
      _: 'HedMobile32',
      sm: 'Hed40'
    },
    fontWeight: {
      _: 300,
      sm: 300
    }
  },
  overviewCard: {
    marginBottom: 7,
    typographyVariant: {
      _: 'Hed40',
      md: 'Hed56'
    }
  }
};
const hedSliderStyles = (variant: BillboardVariants): StyleSystemProps => ({
  ...hedSliderVariants[variant]
});

/* Eyebrow Styles */

const eyebrowVariants: Record<BillboardVariants, StyleSystemProps> = {
  default: {
    typographyVariant: {
      _: 'hed2',
      xs: 'hed3',
      sm2: 'hed4',
      md3: 'hed5',
      xl: 'display1'
    },
    marginBottom: 0
  },
  overviewSlider: {
    typographyVariant: {
      _: 'hed2',
      xs: 'hed3',
      sm2: 'hed4',
      md3: 'hed5',
      xl: 'display1'
    },
    marginBottom: 0
  },
  overviewCard: {
    typographyVariant: {
      _: 'hed2',
      xs: 'hed3',
      sm2: 'hed3',
      md3: 'hed3',
      xl: 'hed3'
    },
    marginBottom: 3
  }
};
const eyebrowStyles = (variant: BillboardVariants): StyleSystemProps => ({
  ...eyebrowVariants[variant],
  fontWeight: ['semibold', null, 'semibold']
});

/* Eyebrow Slider Styles */

const eyebrowSliderVariant: Record<BillboardVariants, StyleSystemProps> = {
  default: {
    typographyVariant: {
      _: 'hed2',
      md: 'hed3'
    }
  },
  overviewSlider: {
    typographyVariant: {
      _: 'hed1',
      sm: 'hed2',
      md: 'hed3'
    }
  },
  overviewCard: {
    typographyVariant: {
      _: 'hed2',
      md: 'hed3'
    }
  }
};
const eyebrowSliderStyles = (variant: BillboardVariants): StyleSystemProps => ({
  ...eyebrowSliderVariant[variant],
  marginBottom: 3
});

const subtitleSliderStyles: StyleSystemProps = {
  typographyVariant: {
    _: 'body1',
    sm: 'body2',
    md: 'body3'
  }
};

/* Buttons Container Styles */

const buttonsContainerVariants: Record<BillboardVariants, StyleSystemProps> = {
  default: {
    paddingY: pxToRem(10)
  },
  overviewSlider: {
    paddingTop: {
      _: 5,
      sm: 7,
      md: 5
    },
    paddingBottom: 5
  },
  overviewCard: {
    paddingY: 5
  }
};
const buttonsContainerStyles = (variant: BillboardVariants, colorMode: BillboardColorMode, styles?: CSSObject): CSSObject => ({
  ...buttonsContainerVariants[variant],
  ...colorModes[colorMode],
  transition: animationTransition('color'),
  maxWidth: '100%',
  typeVariant: ['body1', null, 'Dek20', null, 'Dek24'],
  width: ['100%', null, pxToRem(552), pxToRem(648)],
  ['& button']: {
    margin: 3,
    _hover: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)'
    }
  },
  ...styles
});

export const styles = {
  container: containerStyles,
  content: contentStyles,
  blockText: blockTextStyles,
  eyebrow: eyebrowStyles,
  hed: hedStyles,
  eyebrowSlider: eyebrowSliderStyles,
  hedSlider: hedSliderStyles,
  subtitleSlider: subtitleSliderStyles,
  buttonsContainer: buttonsContainerStyles
};
